import { useEffect, useRef, useState } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';
import expenseReportsService from '../../services/expenseReportsService';
import { usDate } from './dateRange';
import {
  ATTENDEE_CATEGORIES, CATEGORIES, currentReceipts, fmtCents, fmtRate, mileageCents, rateFor, todayIso,
} from './helpers';
import ReceiptPicker, {
  createReceiptFields, initialReceiptState, receiptChangeFor, receiptProblem,
} from './ReceiptPicker';

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const MILES_PATTERN = /^\d+(\.\d)?$/;
const MAX_MILES = 5000;

const fromExpense = (expense) =>
  expense
    ? {
        expense_date: expense.expense_date,
        amount: expense.miles_tenths != null ? '' : (expense.amount_cents / 100).toFixed(2),
        miles: expense.miles_tenths != null ? String(expense.miles_tenths / 10) : '',
        category: expense.category,
        merchant: expense.merchant,
        description: expense.description,
        attendees: expense.attendees || '',
      }
    : { expense_date: todayIso(), amount: '', miles: '', category: '', merchant: '', description: '', attendees: '' };

const tenthsOf = (miles) => Math.round(Number(miles) * 10);

/**
 * Add or edit one expense. `onSave(values, receiptChange)` should resolve on success and throw
 * on failure; `error` is shown above the buttons.
 *
 * Mileage on a date with an IRS rate is claimed in miles; the service prices it,
 * and the amount shown here is the same arithmetic as a preview.
 */
const ExpenseFormModal = ({ expense, onSave, onClose, saving, error }) => {
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);
  const [values, setValues] = useState(() => fromExpense(expense));
  const [localError, setLocalError] = useState('');
  // null while loading; [] if they could not be loaded.
  const [rates, setRates] = useState(null);
  const [ratesError, setRatesError] = useState('');
  const editing = Boolean(expense);
  const current = expense ? currentReceipts(expense) : [];
  const [receiptState, setReceiptState] = useState(() => initialReceiptState(expense, current));
  const uploading = receiptState.uploads.some((u) => u.status === 'uploading');

  useEffect(() => {
    let active = true;
    expenseReportsService
      .getMileageRates()
      .then((loaded) => active && setRates(loaded))
      .catch(() => {
        if (!active) return;
        setRates([]);
        setRatesError("Couldn't load the IRS mileage rates. Close this form and try again.");
      });
    return () => {
      active = false;
    };
  }, []);

  const isMileage = values.category === 'Mileage';
  const rate = isMileage && rates ? rateFor(rates, values.expense_date) : null;
  const claimByMiles = Boolean(rate);
  const showsAttendees = ATTENDEE_CATEGORIES.includes(values.category);
  const mileageBlocked = isMileage && (rates === null || Boolean(ratesError));

  const set = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => {
      const next = { ...prev, [field]: value };
      // Clear what no longer applies, so a stale value can't reappear or be sent.
      if (field === 'category') {
        if (value !== 'Mileage') next.miles = '';
        if (!ATTENDEE_CATEGORIES.includes(value)) next.attendees = '';
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (values.expense_date > todayIso()) {
      setLocalError("The expense date can't be in the future.");
      return;
    }
    const payload = {
      expense_date: values.expense_date,
      category: values.category,
      merchant: values.merchant.trim(),
      description: values.description.trim(),
    };
    if (claimByMiles) {
      const miles = values.miles.trim();
      if (!MILES_PATTERN.test(miles) || Number(miles) <= 0 || Number(miles) > MAX_MILES) {
        setLocalError(`Enter the miles driven, for example 226 or 12.5 (up to ${MAX_MILES.toLocaleString()}).`);
        return;
      }
      payload.miles = miles;
    } else {
      const amount = values.amount.trim().replace(/^\$/, '').replace(/,/g, '');
      if (!AMOUNT_PATTERN.test(amount) || Number(amount) <= 0) {
        setLocalError('Enter the amount in dollars, for example 42.50.');
        return;
      }
      payload.amount = amount;
    }
    if (showsAttendees && values.attendees.trim()) payload.attendees = values.attendees.trim();
    const receiptError = receiptProblem(receiptState, current);
    if (receiptError) {
      setLocalError(receiptError);
      return;
    }
    setLocalError('');
    if (editing) {
      await onSave(payload, receiptChangeFor(receiptState, current, expense));
    } else {
      await onSave({ ...payload, ...createReceiptFields(receiptState) });
    }
  };

  const milesValid = MILES_PATTERN.test(values.miles.trim()) && Number(values.miles) > 0;
  const shownError = localError || error;

  return (
    <div
      ref={dialogRef}
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expenseFormTitle"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="expenseFormTitle">
              <i className={`bi ${editing ? 'bi-pencil' : 'bi-plus-circle'} me-2`}></i>
              {editing ? 'Edit expense' : 'New expense'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} aria-label="Close"></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-sm-6">
                  <label htmlFor="expenseDate" className="form-label">Date *</label>
                  <input
                    id="expenseDate"
                    type="date"
                    className="form-control"
                    value={values.expense_date}
                    max={todayIso()}
                    onChange={set('expense_date')}
                    required
                    disabled={saving}
                  />
                </div>
                <div className="col-sm-6">
                  <label htmlFor="expenseCategory" className="form-label">Category *</label>
                  <select
                    id="expenseCategory"
                    className="form-select"
                    value={values.category}
                    onChange={set('category')}
                    required
                    disabled={saving}
                  >
                    <option value="">Choose a category…</option>
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  {claimByMiles ? (
                    <>
                      <label htmlFor="expenseMiles" className="form-label">Miles driven *</label>
                      <div className="input-group">
                        <input
                          id="expenseMiles"
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          placeholder="0"
                          value={values.miles}
                          onChange={set('miles')}
                          required
                          disabled={saving}
                        />
                        <span className="input-group-text">mi</span>
                      </div>
                      <div className="form-text" data-testid="mileage-preview">
                        {milesValid
                          ? `= ${fmtCents(mileageCents(tenthsOf(values.miles), rate.rate_millis))} at ${fmtRate(rate.rate_millis)}/mile`
                          : `Paid at ${fmtRate(rate.rate_millis)}/mile`}
                        {` (IRS rate from ${usDate(rate.effective_from)})`}
                      </div>
                    </>
                  ) : (
                    <>
                      <label htmlFor="expenseAmount" className="form-label">Amount *</label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          id="expenseAmount"
                          type="text"
                          inputMode="decimal"
                          className="form-control"
                          placeholder="0.00"
                          value={values.amount}
                          onChange={set('amount')}
                          required
                          disabled={saving || mileageBlocked}
                        />
                      </div>
                      {isMileage && rates === null && <div className="form-text">Loading the IRS mileage rates…</div>}
                      {isMileage && rates !== null && !ratesError && (
                        <div className="form-text">No IRS mileage rate is on file for this date, so enter the amount.</div>
                      )}
                      {isMileage && ratesError && <div className="form-text text-danger">{ratesError}</div>}
                    </>
                  )}
                </div>
                <div className="col-12">
                  <label htmlFor="expenseMerchant" className="form-label">Vendor *</label>
                  <input
                    id="expenseMerchant"
                    type="text"
                    className="form-control"
                    placeholder={isMileage ? 'Personal vehicle' : 'Merchant or vendor'}
                    maxLength={200}
                    value={values.merchant}
                    onChange={set('merchant')}
                    required
                    disabled={saving}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor="expenseDescription" className="form-label">Business purpose *</label>
                  <textarea
                    id="expenseDescription"
                    className="form-control"
                    rows="3"
                    maxLength={1000}
                    placeholder={isMileage ? 'Where you drove and why' : 'What it was for'}
                    value={values.description}
                    onChange={set('description')}
                    required
                    disabled={saving}
                  />
                </div>
                {showsAttendees && (
                  <div className="col-12">
                    <label htmlFor="expenseAttendees" className="form-label">Attendees</label>
                    <input
                      id="expenseAttendees"
                      type="text"
                      className="form-control"
                      placeholder="Names of the clients, prospects or others who attended"
                      maxLength={500}
                      value={values.attendees}
                      onChange={set('attendees')}
                      disabled={saving}
                    />
                    <div className="form-text">The reimbursement policy asks for attendees on client and business development meals.</div>
                  </div>
                )}
                <div className="col-12">
                  <label htmlFor="expenseReceiptsFiles" className="form-label">Receipts *</label>
                  <ReceiptPicker
                    current={current}
                    state={receiptState}
                    setState={setReceiptState}
                    getLink={(receipt, disposition) => expenseReportsService.myReceiptLink(expense, receipt.receipt_id, disposition)}
                    idPrefix="expenseReceipts"
                  />
                </div>
              </div>
              {shownError && (
                <div className="alert alert-danger mt-3 mb-0" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {shownError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving || mileageBlocked || uploading}>
                {saving ? 'Saving…' : uploading ? 'Uploading…' : editing ? 'Save changes' : 'Submit expense'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseFormModal;
